import type { FC } from 'react';
import { Trophy } from 'lucide-react';
import type { GameLeaderboardEntry } from '@lunit/shared-types';

interface PodiumCardProps {
  entry: GameLeaderboardEntry;
  rank: 1 | 2 | 3;
}

const PODIUM_HEIGHTS: Record<PodiumCardProps['rank'], string> = {
  1: 'h-32',
  2: 'h-24',
  3: 'h-20'
};

const PODIUM_COLORS: Record<PodiumCardProps['rank'], string> = {
  1: 'from-yellow-400 to-yellow-600',
  2: 'from-gray-300 to-gray-500',
  3: 'from-amber-600 to-amber-800'
};

const MEDAL_COLORS: Record<PodiumCardProps['rank'], string> = {
  1: 'text-yellow-400',
  2: 'text-gray-300',
  3: 'text-amber-600'
};

const formatCoins = (value: string) => {
  const negative = value.startsWith('-');
  const digits = negative ? value.slice(1) : value;
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return negative ? `-${formatted}` : formatted;
};

export const PodiumCard: FC<PodiumCardProps> = ({ entry, rank }) => {
  const isFirst = rank === 1;
  const initials = entry.user.displayName?.charAt(0)?.toUpperCase() || 'P';
  const coins = formatCoins(entry.coins);

  return (
    <div className="flex flex-col items-center">
      <div className="relative mb-2">
        <div
          className={`flex items-center justify-center rounded-full bg-gradient-to-br ${PODIUM_COLORS[rank]}
            text-white font-bold transition-transform ${isFirst ? 'w-20 h-20 text-2xl' : 'w-16 h-16 text-xl'}`}
        >
          {initials}
        </div>
        <div className="absolute -top-1 -right-1">
          <Trophy className={`${MEDAL_COLORS[rank]} ${isFirst ? 'w-8 h-8' : 'w-6 h-6'}`} />
        </div>
      </div>

      <div className={`text-center mb-2 ${isFirst ? 'text-lg font-bold' : 'text-sm font-semibold'}`}>
        {entry.user.displayName}
      </div>

      <div
        className={`${PODIUM_HEIGHTS[rank]} w-20 bg-gradient-to-b ${PODIUM_COLORS[rank]}
          rounded-t-lg flex flex-col items-center justify-start pt-2 shadow-lg transition-transform hover:scale-105`}
      >
        <div className="text-white font-bold text-2xl">{rank}</div>
        <div className="text-white text-xs mt-1">🪙 {coins}</div>
      </div>
    </div>
  );
};

